import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import {
    ExportFormType,
    ExportModalReturnType,
    Format,
} from '@connect-services/client/components/export-modal/export-modal.model';
import { ExportModalAdvancedOptionsComponent } from '@connect-services/client/components/export-modal/export-modal-advanced-options/export-modal-advanced-options.component';
import { ExportModalSimpleOptionsComponent } from '@connect-services/client/components/export-modal/export-modal-simple-options/export-modal-simple-options.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { FieldInterventionExportDto, FieldInterventionForm, Person } from '@connect-services/client/sdk/api';
import {
    ButtonComponent,
    ButtonType,
    IconPosition,
} from '@connect-services/src/lib/components/button/button.component';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        ComplexModalTemplate,
        ExportModalAdvancedOptionsComponent,
        ExportModalSimpleOptionsComponent,
        RessourcesModule,
    ],
    selector: 'app-export-modal',
    templateUrl: './export-modal.component.html',
})
export class ExportModalComponent implements OnInit {
    public formGroup: FormGroup<ExportFormType> | null = null;
    public readonly isAdvancedOptionsOpen: WritableSignal<boolean> = signal(false);

    protected readonly ButtonType = ButtonType;
    protected readonly CheckboxState = CheckboxState;
    protected dialogRef = inject(MatDialogRef<ExportModalComponent>);
    protected readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    protected readonly IconPosition = IconPosition;

    private readonly _defaultSelectedStatus = [
        FieldInterventionExportDto.StatusEnum.DONE,
        FieldInterventionExportDto.StatusEnum.TO_VALIDATE,
        FieldInterventionExportDto.StatusEnum.INVALID,
    ];
    private readonly formatToEnum: Record<Format, FieldInterventionExportDto.FormatsEnum> = {
        Geopackage: FieldInterventionExportDto.FormatsEnum.GEOPACKAGE,
        KMZ: FieldInterventionExportDto.FormatsEnum.KMZ,
        Shapefile: FieldInterventionExportDto.FormatsEnum.SHAPEFILE,
        XLSX: FieldInterventionExportDto.FormatsEnum.XLSX,
        advancedXlsx: FieldInterventionExportDto.FormatsEnum.ADVANCED_XLSX,
    };

    public closeModal(event: ModalResultEnum): Promise<void> | void {
        return this.dialogRef.close(
            event === ModalResultEnum.CONFIRM
                ? this.getExportModalReturnValue(ModalResultEnum.CONFIRM)
                : { type: ModalResultEnum.CANCEL },
        );
    }

    public ngOnInit(): void {
        this.formGroup = this.initForm();
    }

    private getExportModalReturnValue(type: ModalResultEnum = ModalResultEnum.CANCEL): ExportModalReturnType {
        if (!this.formGroup) {
            return { type };
        }

        const formValues = this.formGroup.getRawValue();
        const returnObject: ExportModalReturnType = { type };

        if (formValues.startDate) {
            returnObject.startDate = formValues.startDate;
        }

        if (formValues.endDate) {
            returnObject.endDate = formValues.endDate;
        }

        if (formValues.assignedPeoples.length) {
            returnObject.peopleIds = formValues.assignedPeoples.map((person) => person.id);
        }

        if (formValues.intervenedPeoples.length) {
            returnObject.controlledByPeople = formValues.intervenedPeoples.map((person) => person.id);
        }

        if (formValues.forms.length) {
            returnObject.formIds = formValues.forms.map((form: FieldInterventionForm) => form.id);
        }

        if (formValues.status.length) {
            returnObject.status = formValues.status;
        }

        if (formValues.formats.length) {
            returnObject.formats = formValues.formats.map((format) => this.formatToEnum[format]);
        }

        if (formValues.includeUpdatedEntities) {
            returnObject.includeUpdatedEntities = formValues.includeUpdatedEntities;
        }

        return returnObject;
    }

    private initForm(): FormGroup<ExportFormType> {
        return new FormGroup<ExportFormType>({
            assignedPeoples: new FormControl<Person[]>([], {
                nonNullable: true,
            }),
            endDate: new FormControl<Date | null>(null),
            formats: new FormControl<Format[]>([], {
                nonNullable: true,
                validators: [Validators.required],
            }),
            forms: new FormControl<FieldInterventionForm[]>([], {
                nonNullable: true,
            }),
            includeUpdatedEntities: new FormControl<boolean>(false, {
                nonNullable: true,
            }),
            intervenedPeoples: new FormControl<Person[]>([], {
                nonNullable: true,
            }),
            startDate: new FormControl<Date | null>(null),
            status: new FormControl<FieldInterventionExportDto.StatusEnum[]>([...this._defaultSelectedStatus], {
                nonNullable: true,
                validators: [Validators.required],
            }),
        });
    }
}
